import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useBU } from "./BUContext";
import OfferCardActions from "./OfferCardActions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Visual constants
// ---------------------------------------------------------------------------
const COLUMN_META = {
  opportunity: { accent: "border-violet-500/30", dot: "bg-violet-400", desc: "Tracked Ghost Recon opportunities. Click a card to add child offers." },
  offers: { accent: "border-slate-500/30", dot: "bg-slate-400", desc: "Newly promoted or just discovered." },
  in_production: { accent: "border-blue-500/30", dot: "bg-blue-400", desc: "Assigned, in creative, in production." },
  live: { accent: "border-emerald-500/30", dot: "bg-emerald-400", desc: "Spending money in market." },
  paused: { accent: "border-amber-500/30", dot: "bg-amber-400", desc: "Spend halted, decision required." },
  closed: { accent: "border-slate-700", dot: "bg-slate-600", desc: "Wound down." },
};

const TREND_META = {
  GROWING: { color: "text-emerald-300", emoji: "↑" },
  STABLE: { color: "text-slate-300", emoji: "→" },
  DECLINING: { color: "text-rose-300", emoji: "↓" },
  UNKNOWN: { color: "text-slate-500", emoji: "·" },
};

const FLAG_META = {
  needs_attention: { color: "text-red-400 border-red-500/40 bg-red-500/10", emoji: "🔴", label: "Needs Attention" },
  decision_needed: { color: "text-amber-300 border-amber-500/40 bg-amber-500/10", emoji: "🟡", label: "Decision Needed" },
  scale_candidate: { color: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", emoji: "🟢", label: "Scale Candidate" },
};

const STATUS_LABEL = {
  pending: "pending",
  discovered: "discovered",
  assigned: "assigned",
  activated: "activated",
  in_creative: "in creative",
  in_market: "live",
  paused: "paused",
  closed: "closed",
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const PipelineBoard = () => {
  const { currentBU } = useBU();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [hoverColumn, setHoverColumn] = useState(null);
  const [toast, setToast] = useState(null);
  const [openOpportunityId, setOpenOpportunityId] = useState(null);

  const showToast = useCallback((msg, tone = "ok") => {
    setToast({ msg, tone });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    if (!currentBU) return;
    try {
      const res = await axios.get(`${API}/pipeline/board?business_unit=${encodeURIComponent(currentBU)}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  }, [currentBU]);

  useEffect(() => { load(); }, [load]);

  const moveOffer = useCallback(async (offerId, sourceColumn, targetColumn) => {
    if (sourceColumn === targetColumn || !data) return;

    // Optimistic update — move card immediately so UI feels instant
    const next = JSON.parse(JSON.stringify(data));
    let card = null;
    for (const col of next.columns) {
      const idx = col.cards.findIndex((c) => c.offer_id === offerId);
      if (idx !== -1) {
        [card] = col.cards.splice(idx, 1);
        col.count = col.cards.length;
        break;
      }
    }
    if (!card) return;
    const target = next.columns.find((c) => c.key === targetColumn);
    if (!target) return;
    target.cards.unshift(card);
    target.count = target.cards.length;
    setData(next);

    try {
      await axios.post(`${API}/pipeline/move`, { offer_id: offerId, target_column: targetColumn });
      // Refetch to pick up server-side flag recomputation, status, days, etc.
      load();
    } catch (e) {
      showToast(e.response?.data?.detail || "Move failed", "err");
      load(); // revert by reloading authoritative state
    }
  }, [data, load, showToast]);

  // Change 2 — soft-delete (dismiss) an opportunity. Optimistic UI update
  // so the card vanishes immediately; on failure we re-fetch from server.
  const dismissOpportunity = useCallback(async (opportunityId) => {
    if (!data) return;
    const prev = data;
    setData((d) => ({
      ...d,
      columns: d.columns.map((col) =>
        col.key === "opportunity"
          ? {
              ...col,
              cards: col.cards.filter((c) => c.opportunity_id !== opportunityId),
              count: col.cards.filter((c) => c.opportunity_id !== opportunityId).length,
            }
          : col,
      ),
    }));
    try {
      await axios.post(`${API}/pipeline/opportunities/${opportunityId}/dismiss`);
      showToast("Opportunity removed from pipeline");
    } catch (e) {
      // Restore on failure
      setData(prev);
      showToast(e.response?.data?.detail || "Failed to dismiss", "err");
    }
  }, [data, showToast]);

  // Change 3 — inline rename. Called by OpportunityDetailPanel on save.
  // Optimistically updates the card name in the kanban without a refetch.
  const renameOpportunity = useCallback((opportunityId, newName) => {
    setData((d) => d ? ({
      ...d,
      columns: d.columns.map((col) =>
        col.key === "opportunity"
          ? {
              ...col,
              cards: col.cards.map((c) =>
                c.opportunity_id === opportunityId ? { ...c, name: newName } : c
              ),
            }
          : col,
      ),
    }) : d);
  }, []);

  if (!currentBU) {
    return (
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-12 text-center" data-testid="pipeline-no-bu">
        <p className="text-slate-300">Select a workspace to view the pipeline.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pipeline-board">
      {/* Header */}
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-white" data-testid="pipeline-title">Pipeline</h1>
          <p className="text-sm text-slate-400 mt-1">
            {data?.total_offers ?? 0} offers in <span className="text-slate-200 font-medium">{currentBU}</span> workspace
          </p>
        </div>
        {data && (
          <div className="flex items-center gap-2 text-xs" data-testid="pipeline-flag-totals">
            <FlagPill kind="needs_attention" count={data.flag_totals?.needs_attention || 0} />
            <FlagPill kind="decision_needed" count={data.flag_totals?.decision_needed || 0} />
          </div>
        )}
      </header>

      {error && (
        <div className="bg-red-950/40 border border-red-500/30 text-red-200 text-sm rounded-lg p-4" data-testid="pipeline-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center text-slate-500 py-16" data-testid="pipeline-loading">Loading pipeline…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-6 gap-4" data-testid="pipeline-columns">
          {(data?.columns || []).map((col) => (
            <Column
              key={col.key}
              column={col}
              draggingId={draggingId}
              hoverColumn={hoverColumn}
              onDragStart={(id) => setDraggingId(id)}
              onDragEnd={() => { setDraggingId(null); setHoverColumn(null); }}
              onColumnEnter={(key) => setHoverColumn(key)}
              onDrop={(offerId, sourceColumn) => moveOffer(offerId, sourceColumn, col.key)}
              onOpportunityClick={(oppId) => setOpenOpportunityId(oppId)}
              onOpportunityDismiss={dismissOpportunity}
            />
          ))}
        </div>
      )}

      {openOpportunityId && (
        <OpportunityDetailPanel
          opportunityId={openOpportunityId}
          onClose={() => setOpenOpportunityId(null)}
          onChange={load}
          onRename={renameOpportunity}
          showToast={showToast}
        />
      )}

      {toast && (
        <div
          data-testid="pipeline-toast"
          className={`fixed bottom-6 right-6 px-4 py-3 rounded-lg border shadow-lg max-w-sm text-sm
            ${toast.tone === "err" ? "bg-red-950/90 border-red-500/40 text-red-200" : "bg-emerald-950/90 border-emerald-500/40 text-emerald-200"}`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Column
// ---------------------------------------------------------------------------
const Column = ({ column, draggingId, hoverColumn, onDragStart, onDragEnd, onColumnEnter, onDrop, onOpportunityClick, onOpportunityDismiss }) => {
  const meta = COLUMN_META[column.key] || COLUMN_META.offers;
  const isOpportunityColumn = column.key === "opportunity";
  const isHover = hoverColumn === column.key && draggingId && !isOpportunityColumn;

  const handleDragOver = (e) => {
    // Disable drop on the opportunity column — opportunities only get there
    // via the Track flow.
    if (isOpportunityColumn) return;
    e.preventDefault();
    onColumnEnter(column.key);
  };

  const handleDrop = (e) => {
    if (isOpportunityColumn) return;
    e.preventDefault();
    const data = e.dataTransfer.getData("application/json");
    if (!data) return;
    try {
      const { offer_id, source_column } = JSON.parse(data);
      if (offer_id) onDrop(offer_id, source_column);
    } catch { /* noop */ }
    onDragEnd();
  };

  return (
    <div
      data-testid={`pipeline-column-${column.key}`}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`bg-slate-900/40 border rounded-xl flex flex-col min-h-[400px] transition-colors
        ${isHover ? "border-emerald-500/60 bg-emerald-500/5" : meta.accent}`}
    >
      <header className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`h-2 w-2 rounded-full ${meta.dot}`} />
          <span className="text-sm font-medium text-slate-100">{column.label}</span>
        </div>
        <span className="text-xs text-slate-500" data-testid={`pipeline-column-count-${column.key}`}>{column.count}</span>
      </header>
      <div className="flex-1 p-3 space-y-2 overflow-y-auto" data-testid={`pipeline-column-cards-${column.key}`}>
        {column.cards.length === 0 ? (
          <div className="text-xs text-slate-600 italic px-2 py-4 text-center">
            {isOpportunityColumn ? "No tracked opportunities yet." : (isHover ? "Drop here" : "No offers")}
          </div>
        ) : isOpportunityColumn ? (
          column.cards.map((card) => (
            <OpportunityCard
              key={card.opportunity_id}
              card={card}
              onClick={() => onOpportunityClick?.(card.opportunity_id)}
              onDismiss={onOpportunityDismiss}
            />
          ))
        ) : (
          column.cards.map((card) => (
            <Card
              key={card.offer_id}
              card={card}
              sourceColumn={column.key}
              dragging={draggingId === card.offer_id}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            />
          ))
        )}
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Opportunity Card (Pipeline Opportunity stage — Fix 1)
// ---------------------------------------------------------------------------
const OpportunityCard = ({ card, onClick, onDismiss }) => {
  const trend = TREND_META[card.trend] || TREND_META.UNKNOWN;
  const [confirmingDismiss, setConfirmingDismiss] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const handleDismissClick = (e) => {
    e.stopPropagation();
    setConfirmingDismiss(true);
  };

  const handleConfirmYes = async (e) => {
    e.stopPropagation();
    if (dismissing) return;
    setDismissing(true);
    try {
      await onDismiss?.(card.opportunity_id);
      // Card removed from data by parent — no further local state needed.
    } finally {
      setDismissing(false);
      setConfirmingDismiss(false);
    }
  };

  const handleConfirmCancel = (e) => {
    e.stopPropagation();
    setConfirmingDismiss(false);
  };

  return (
    <div
      data-testid={`pipeline-opportunity-card-${card.opportunity_id}`}
      onClick={() => !confirmingDismiss && onClick?.()}
      className="relative cursor-pointer rounded-lg p-3 border border-violet-500/30 bg-slate-950/60 hover:border-violet-400/60 hover:bg-slate-900/60 transition-colors"
    >
      {!confirmingDismiss && (
        <button
          type="button"
          onClick={handleDismissClick}
          data-testid={`pipeline-opportunity-dismiss-btn-${card.opportunity_id}`}
          title="Remove from pipeline"
          className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full text-slate-500 hover:text-rose-300 hover:bg-rose-500/10 flex items-center justify-center text-xs leading-none transition-colors"
        >
          ✕
        </button>
      )}
      {confirmingDismiss && (
        <div
          data-testid={`pipeline-opportunity-dismiss-confirm-${card.opportunity_id}`}
          className="absolute inset-0 rounded-lg bg-slate-950/95 border border-rose-500/40 flex flex-col items-center justify-center p-3"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-xs text-rose-200 font-medium">Remove from pipeline?</div>
          <div className="mt-2 flex gap-2">
            <button
              type="button"
              onClick={handleConfirmYes}
              disabled={dismissing}
              data-testid={`pipeline-opportunity-dismiss-yes-${card.opportunity_id}`}
              className="text-xs px-3 py-1 rounded bg-rose-500 hover:bg-rose-400 text-white font-medium disabled:bg-slate-700"
            >
              {dismissing ? "Removing…" : "Yes"}
            </button>
            <button
              type="button"
              onClick={handleConfirmCancel}
              data-testid={`pipeline-opportunity-dismiss-cancel-${card.opportunity_id}`}
              className="text-xs px-3 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      <div className="flex items-start justify-between gap-2 pr-5">
        <div className="text-sm font-semibold text-white leading-tight truncate" data-testid="pipeline-opportunity-name">
          {card.name}
        </div>
        <span className={`text-xs font-mono ${trend.color}`} title={`Trend: ${card.trend}`}>
          {trend.emoji} {card.trend}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-2 text-[11px] text-slate-400">
        <span className="px-2 py-0.5 rounded bg-slate-800/70 text-slate-300 font-mono">
          {card.advertiser_count} advertiser{card.advertiser_count === 1 ? "" : "s"}
        </span>
        <span className="px-2 py-0.5 rounded bg-slate-800/70 text-slate-300 font-mono">
          {card.ad_age_days}d
        </span>
        {typeof card.score === "number" ? (
          <span className="px-2 py-0.5 rounded bg-violet-500/20 text-violet-200 font-mono">
            {card.score}/100
          </span>
        ) : (
          <span className="px-2 py-0.5 rounded bg-slate-800/70 text-slate-500 font-mono text-[10px]">
            {card.score}
          </span>
        )}
      </div>
      {card.child_offer_count > 0 && (
        <div className="mt-2 text-[11px] text-slate-400">
          {card.child_offer_count} child offer{card.child_offer_count === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Opportunity Detail Panel (Fix 1 — clicked-into view + Add Offer)
// ---------------------------------------------------------------------------
const OpportunityDetailPanel = ({ opportunityId, onClose, onChange, onRename, showToast }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddOffer, setShowAddOffer] = useState(false);
  const [form, setForm] = useState({ offer_name: "", network: "", commission: "", run_times: "", daily_cap: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Change 3 — inline rename
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameError, setRenameError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/pipeline/opportunities/${opportunityId}`);
      setDetail(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load opportunity");
    } finally {
      setLoading(false);
    }
  }, [opportunityId]);

  useEffect(() => { load(); }, [load]);

  const startRename = () => {
    setNameDraft(detail?.name || "");
    setRenameError(null);
    setEditingName(true);
  };

  const cancelRename = () => {
    setEditingName(false);
    setNameDraft("");
    setRenameError(null);
  };

  const saveRename = async () => {
    const next = nameDraft.trim();
    if (!next) { setRenameError("Name is required."); return; }
    if (next.length > 120) { setRenameError("Name must be 120 characters or fewer."); return; }
    if (detail && next === detail.name) { setEditingName(false); return; }
    setRenameBusy(true);
    setRenameError(null);
    try {
      const res = await axios.patch(`${API}/pipeline/opportunities/${opportunityId}`, { name: next });
      const saved = res.data?.name || next;
      setDetail((d) => d ? { ...d, name: saved } : d);
      onRename?.(opportunityId, saved);
      setEditingName(false);
      showToast?.("Opportunity renamed");
    } catch (e) {
      setRenameError(e.response?.data?.detail || "Rename failed");
    } finally {
      setRenameBusy(false);
    }
  };


  const submit = async () => {
    setError(null);
    if (!form.offer_name.trim()) {
      setError("Offer name is required.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        offer_name: form.offer_name.trim(),
        network: form.network.trim(),
        commission: form.commission ? Number(form.commission) : null,
        run_times: form.run_times.trim(),
        daily_cap: form.daily_cap ? Number(form.daily_cap) : null,
      };
      await axios.post(`${API}/pipeline/opportunities/${opportunityId}/offers`, payload);
      showToast?.("Offer added to opportunity");
      setShowAddOffer(false);
      setForm({ offer_name: "", network: "", commission: "", run_times: "", daily_cap: "" });
      await load();
      onChange?.();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to add offer");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="pipeline-opportunity-detail" onClick={onClose}>
      <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <header className="sticky top-0 z-10 bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="text-xs uppercase tracking-wider text-violet-400">Opportunity</div>
            {!editingName ? (
              <h2
                className="text-lg font-semibold text-white truncate cursor-text hover:bg-slate-800/50 -mx-2 px-2 rounded inline-block max-w-full"
                onClick={() => detail && startRename()}
                title="Click to rename"
                data-testid="pipeline-opportunity-detail-name"
              >
                {detail?.name || "Loading…"}
              </h2>
            ) : (
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="text"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveRename();
                    if (e.key === "Escape") cancelRename();
                  }}
                  maxLength={120}
                  autoFocus
                  data-testid="pipeline-opportunity-rename-input"
                  className="flex-1 px-2 py-1 rounded bg-slate-950 border border-slate-700 focus:border-emerald-500 focus:outline-none text-base font-semibold text-white"
                />
                <button
                  type="button"
                  onClick={saveRename}
                  disabled={renameBusy}
                  data-testid="pipeline-opportunity-rename-save"
                  title="Save (Enter)"
                  className="px-2 py-1 rounded bg-emerald-500 hover:bg-emerald-400 disabled:bg-slate-700 text-slate-950 text-sm font-bold"
                >
                  ✓
                </button>
                <button
                  type="button"
                  onClick={cancelRename}
                  data-testid="pipeline-opportunity-rename-cancel"
                  title="Cancel (Esc)"
                  className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm"
                >
                  ✕
                </button>
              </div>
            )}
            {renameError && (
              <div className="mt-1 text-xs text-rose-300" data-testid="pipeline-opportunity-rename-error">{renameError}</div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-sm shrink-0" data-testid="pipeline-opportunity-detail-close">✕ Close</button>
        </header>
        {loading && <div className="p-12 text-center text-slate-500">Loading…</div>}
        {!loading && detail && (
          <div className="p-6 space-y-6 text-sm text-slate-300">
            {/* Metrics row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric label="Trend" value={`${TREND_META[detail.trend]?.emoji || "·"} ${detail.trend}`} />
              <Metric label="Advertisers" value={detail.advertiser_count} />
              <Metric label="Ad age" value={`${detail.ad_age_days}d`} />
              <Metric label="Score" value={typeof detail.score === "number" ? `${detail.score}/100` : detail.score} />
            </div>

            {/* Brief summary */}
            <section>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Brief summary</div>
              <div className="rounded-lg bg-slate-950/60 border border-slate-800 px-4 py-3 text-slate-200 whitespace-pre-wrap" data-testid="pipeline-opportunity-detail-brief">
                {detail.brief_summary || <span className="text-slate-500 italic">(no brief recorded)</span>}
              </div>
            </section>

            {/* Full Ghost Recon report */}
            {detail.ghost_recon_report && (
              <section>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Ghost Recon report</div>
                <pre className="rounded-lg bg-slate-950/60 border border-slate-800 px-4 py-3 text-xs text-slate-300 whitespace-pre-wrap overflow-x-auto max-h-72" data-testid="pipeline-opportunity-detail-report">
                  {detail.ghost_recon_report}
                </pre>
              </section>
            )}

            {/* Child offers */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs uppercase tracking-wider text-slate-500">Child offers</div>
                <button
                  data-testid="pipeline-opportunity-add-offer-btn"
                  onClick={() => setShowAddOffer((v) => !v)}
                  className="text-xs px-3 py-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 hover:bg-emerald-500/20"
                >
                  {showAddOffer ? "Cancel" : "+ Add Offer"}
                </button>
              </div>
              {detail.offers.length === 0 ? (
                <div className="rounded-lg bg-slate-950/60 border border-slate-800 px-4 py-3 text-slate-500 italic text-xs">
                  No offers yet. Use "Add Offer" to create the first one.
                </div>
              ) : (
                <div className="space-y-2">
                  {detail.offers.map((o) => (
                    <div key={o.offer_id} className="flex items-center justify-between rounded-md bg-slate-950/60 border border-slate-800 px-4 py-2" data-testid={`pipeline-opportunity-child-${o.offer_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm text-white truncate">{o.name}</div>
                        <div className="text-[11px] text-slate-500 font-mono">{o.offer_code}</div>
                      </div>
                      <div className="text-[11px] text-slate-400">{o.status}</div>
                    </div>
                  ))}
                </div>
              )}

              {showAddOffer && (
                <div className="mt-3 rounded-lg bg-slate-950/60 border border-slate-800 p-4 space-y-3" data-testid="pipeline-opportunity-add-offer-form">
                  <Field label="Offer name *" testid="add-offer-name" value={form.offer_name} onChange={(v) => setForm((f) => ({ ...f, offer_name: v }))} />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Network" testid="add-offer-network" value={form.network} onChange={(v) => setForm((f) => ({ ...f, network: v }))} />
                    <Field label="Commission ($)" testid="add-offer-commission" type="number" value={form.commission} onChange={(v) => setForm((f) => ({ ...f, commission: v }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Run times" testid="add-offer-runtimes" value={form.run_times} onChange={(v) => setForm((f) => ({ ...f, run_times: v }))} />
                    <Field label="Daily cap ($)" testid="add-offer-cap" type="number" value={form.daily_cap} onChange={(v) => setForm((f) => ({ ...f, daily_cap: v }))} />
                  </div>
                  {error && <div className="text-xs text-rose-300" data-testid="pipeline-opportunity-add-offer-error">{error}</div>}
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setShowAddOffer(false)} className="text-xs text-slate-400 hover:text-white px-3 py-1.5">Cancel</button>
                    <button onClick={submit} disabled={submitting} data-testid="pipeline-opportunity-add-offer-submit" className="text-xs px-4 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:bg-slate-700 disabled:text-slate-500 text-slate-950 font-semibold">
                      {submitting ? "Saving…" : "Save offer"}
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

const Metric = ({ label, value }) => (
  <div className="rounded-lg bg-slate-950/60 border border-slate-800 px-3 py-2">
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-sm font-semibold text-slate-100 mt-0.5">{value}</div>
  </div>
);

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-wider text-slate-500 mb-1">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full px-3 py-2 rounded-md bg-slate-900 border border-slate-700 text-sm text-white focus:border-emerald-500 focus:outline-none"
    />
  </label>
);

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------
const Card = ({ card, sourceColumn, dragging, onDragStart, onDragEnd }) => {
  const navigate = useNavigate();
  const flagMeta = card.flag ? FLAG_META[card.flag] : null;

  const handleDragStart = (e) => {
    e.dataTransfer.setData("application/json", JSON.stringify({
      offer_id: card.offer_id,
      source_column: sourceColumn,
    }));
    e.dataTransfer.effectAllowed = "move";
    onDragStart(card.offer_id);
  };

  const handleClick = () => {
    if (dragging) return;
    // Bug 3 defensive guard — never navigate to /offers/undefined. Opportunity
    // cards render via <OpportunityCard/> and should never hit this handler.
    // If they ever do (data shape regression), no-op instead of routing.
    if (!card.offer_id) return;
    navigate(`/offers/${card.offer_id}`);
  };

  const score = Math.round(card.current_score || 0);

  return (
    <article
      data-testid={`pipeline-card-${card.offer_id}`}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={onDragEnd}
      onClick={handleClick}
      className={`group bg-slate-900/80 border border-slate-800 rounded-lg p-3 cursor-grab active:cursor-grabbing
        hover:border-emerald-500/50 transition-colors
        ${dragging ? "opacity-40" : ""}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <h3 className="text-sm font-medium text-slate-100 line-clamp-2 flex-1" title={card.name} data-testid={`pipeline-card-name-${card.offer_id}`}>
          {card.name}
        </h3>
        {flagMeta && (
          <span
            className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${flagMeta.color}`}
            title={flagMeta.label}
            data-testid={`pipeline-card-flag-${card.offer_id}`}
          >
            {flagMeta.emoji}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
        <span className="text-slate-500">
          <span className="text-slate-300">{STATUS_LABEL[card.status] || card.status}</span>
        </span>
        <span title="Days in current stage">
          <span className="text-slate-300">{card.days_in_stage}d</span>
        </span>
        {typeof card.payout === "number" && card.payout > 0 && (
          <span>
            <span className="text-emerald-400">${card.payout.toFixed(0)}</span>
            <span className="text-slate-600 ml-0.5">{card.payout_type || "CPA"}</span>
          </span>
        )}
        {score > 0 && (
          <span title="Opportunity score">
            <span className="text-slate-300">{score}</span>
            <span className="text-slate-600 ml-0.5">scr</span>
          </span>
        )}
        {card.source_platform && (
          <span className="truncate max-w-[80px]" title={card.source_platform}>
            <span className="text-slate-500">{card.source_platform}</span>
          </span>
        )}
      </div>

      {/* Additive: product_name, funnel_url, offer_code, dates + edit/delete */}
      <OfferCardActions offer={card} />
    </article>
  );
};

// ---------------------------------------------------------------------------
// Tiny helpers
// ---------------------------------------------------------------------------
const FlagPill = ({ kind, count }) => {
  const meta = FLAG_META[kind];
  return (
    <span
      data-testid={`pipeline-flag-${kind}`}
      className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full border ${meta.color}`}
    >
      <span>{meta.emoji}</span>
      <span className="text-slate-200 font-medium">{count}</span>
      <span className="text-slate-500 hidden sm:inline">{meta.label}</span>
    </span>
  );
};

export default PipelineBoard;
